import type { WhatsAppConfig, WhatsAppDocType } from '@erp/shared';
import { WHATSAPP_DOC_TYPES } from '@erp/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, Card, FormField, Input, Skeleton } from '../../components/ui/index.js';
import { clearWhatsAppConfigCache, getWhatsAppConfig, renderTemplate, updateWhatsAppConfig } from '../../lib/whatsapp.js';

// Sample values for the live preview.
const SAMPLE: Record<string, string> = {
  company: 'Demo Co.', customer: 'Acme Retail', docNumber: 'INV-000123', date: '2026-06-04',
  dueDate: '2026-06-30', total: '$1,500.00', paid: '$500.00', balance: '$1,000.00', currency: 'USD',
  link: 'https://app/i/abc123',
};
const PLACEHOLDERS = ['company', 'customer', 'docNumber', 'date', 'dueDate', 'total', 'paid', 'balance', 'currency', 'link'];

export function WhatsAppSettingsPage(): JSX.Element {
  const { t } = useTranslation('whatsapp');
  const { t: tc } = useTranslation();
  const [config, setConfig] = useState<WhatsAppConfig | null>(null);
  const [dialCode, setDialCode] = useState('');
  const [templates, setTemplates] = useState<Record<WhatsAppDocType, string>>({} as Record<WhatsAppDocType, string>);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const c = await getWhatsAppConfig();
        setConfig(c); setDialCode(c.dialCode); setTemplates(c.templates);
      } catch { toast.error(tc('errors:server')); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      const c = await updateWhatsAppConfig({ dialCode: dialCode.replace(/\D/g, ''), templates });
      clearWhatsAppConfigCache();
      setConfig(c); setDialCode(c.dialCode); setTemplates(c.templates);
      toast.success(tc('actions.saved', { defaultValue: 'Saved' }));
    } catch { toast.error(tc('errors:server')); } finally { setSaving(false); }
  };

  if (!config) {
    return <Card><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;
  }

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('title', { defaultValue: 'WhatsApp' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('subtitle', { defaultValue: 'Send invoices and messages to customers on WhatsApp — one tap, no API needed.' })}</p>
      </header>

      <Card className="flex flex-col gap-4">
        <FormField
          id="wa-dial"
          label={t('dialCode', { defaultValue: 'Default country code' })}
          helperText={t('dialCodeHint', { defaultValue: 'Digits only (e.g. 964 for Iraq, 966 for Saudi). Added to local numbers that start with 0 or have no country code.' })}
        >
          <div className="flex items-center gap-2">
            <span className="text-fg-muted">+</span>
            <Input id="wa-dial" className="w-28 font-mono" inputMode="numeric" value={dialCode} onChange={(e) => setDialCode(e.target.value.replace(/\D/g, ''))} placeholder="964" />
          </div>
        </FormField>

        <div className="rounded-md border border-border-subtle bg-bg-subtle/40 p-3 text-xs text-fg-muted">
          {t('placeholdersHint', { defaultValue: 'Use these placeholders in your templates:' })}{' '}
          {PLACEHOLDERS.map((p) => <code key={p} className="mx-0.5 rounded bg-bg px-1 py-0.5 text-fg">{`{${p}}`}</code>)}
        </div>
      </Card>

      <Card className="flex flex-col gap-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('templates', { defaultValue: 'Message templates' })}</h2>
        {WHATSAPP_DOC_TYPES.map((dt) => (
          <div key={dt} className="grid grid-cols-1 gap-2 lg:grid-cols-2">
            <FormField id={`wa-${dt}`} label={t(`type.${dt}`, { defaultValue: dt })}>
              <textarea
                id={`wa-${dt}`}
                rows={4}
                value={templates[dt] ?? ''}
                onChange={(e) => setTemplates((s) => ({ ...s, [dt]: e.target.value }))}
                className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              />
            </FormField>
            <div>
              <div className="mb-1.5 text-sm font-medium text-fg-muted">{t('preview', { defaultValue: 'Preview' })}</div>
              <div className="min-h-[6rem] whitespace-pre-wrap rounded-lg border border-success/30 bg-success/5 p-3 text-sm" dir="auto">
                {renderTemplate(templates[dt] ?? '', SAMPLE) || <span className="text-fg-muted">—</span>}
              </div>
            </div>
          </div>
        ))}
        <div>
          <Button onClick={save} loading={saving}>{tc('actions.save')}</Button>
        </div>
      </Card>
    </div>
  );
}
